import { LitElement, html, css } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { clockSync } from '../state/clock-sync.js';
import { connectionState } from '../state/connection-state.js';
import { getCurrentMemoryMb } from '../watchdog/memory-monitor.js';
import { tickStore } from '../renderer/tick-store.js';
import { snapshot as logSnapshot, type LogEntry } from './log-buffer.js';
import { clearCacheAndReload, resetPairing, sendSnapshot } from './actions.js';

/**
 * Saha-destek tanı overlay'i — cihaz metrikleri + kurtarma aksiyonları + loglar.
 *
 * Memory: decision_pwa_resilience.md §11
 */
@customElement('fp-diagnostic-overlay')
export class FpDiagnosticOverlay extends LitElement {
    /** PIN doğrulanmadan açıldıysa (cihazda PIN tanımlı değil) yıkıcı işlemler gizli (K4). */
    @property({ type: Boolean }) restricted = false;
    /** Yıkıcı işlemler iki adımlı onay — '' | 'cache' | 'reset'. */
    @state() private confirming: '' | 'cache' | 'reset' = '';
    private confirmTimer?: number;

    @state() private deviceId = '—';
    @state() private gatewayUrl = '—';
    @state() private version = '—';
    @state() private lastTickAge = 0;
    @state() private memMb = 0;
    @state() private network = 'unknown';
    @state() private serverOffset = 0;
    @state() private connection = connectionState.state;
    @state() private logs: LogEntry[] = [];
    @state() private showLogs = false;

    private refreshTimer?: number;

    connectedCallback(): void {
        super.connectedCallback();
        document.documentElement.classList.add('diagnostic-open');

        this.deviceId = decodeDeviceId();
        this.gatewayUrl = (import.meta.env.VITE_GATEWAY_URL as string | undefined) ?? '—';
        this.version = (import.meta.env.VITE_APP_VERSION as string | undefined) ?? 'dev';

        this.refreshTimer = window.setInterval(() => this.refresh(), 1000);
        this.refresh();
    }

    disconnectedCallback(): void {
        super.disconnectedCallback();
        document.documentElement.classList.remove('diagnostic-open');
        if (this.refreshTimer !== undefined) clearInterval(this.refreshTimer);
        if (this.confirmTimer !== undefined) clearTimeout(this.confirmTimer);
    }

    /** Yıkıcı işlem: ilk dokunuş onay ister, ikinci dokunuş çalıştırır (yanlış tıkla yayından düşmeyi önler). */
    private guard(action: 'cache' | 'reset', run: () => void): void {
        if (this.confirming === action) {
            this.confirming = '';
            if (this.confirmTimer !== undefined) clearTimeout(this.confirmTimer);
            run();
            return;
        }
        this.confirming = action;
        if (this.confirmTimer !== undefined) clearTimeout(this.confirmTimer);
        this.confirmTimer = window.setTimeout(() => {
            this.confirming = '';
        }, 4000);
    }

    private refresh(): void {
        const timestamps = tickStore
            .allServerTimestamps()
            .filter((t): t is number => typeof t === 'number');
        this.lastTickAge = timestamps.length
            ? Math.floor(clockSync.ageMs(Math.max(...timestamps)) / 1000)
            : -1;
        this.memMb = Math.round(getCurrentMemoryMb());
        this.serverOffset = Math.round(clockSync.offset);
        this.connection = connectionState.state;
        const conn = (navigator as unknown as { connection?: { effectiveType?: string } }).connection;
        this.network = conn?.effectiveType ?? 'unknown';
        this.logs = logSnapshot();
    }

    static styles = css`
        :host {
            position: fixed;
            inset: 0;
            background: rgba(0, 0, 0, 0.92);
            color: #fff;
            font-family: ui-monospace, monospace;
            font-size: 14px;
            padding: 2rem;
            overflow: auto;
            z-index: 100000;
            cursor: auto;
        }
        h2 { margin: 0 0 1rem; color: #f59e0b; }
        table { width: 100%; max-width: 760px; border-collapse: collapse; }
        td { padding: 0.5rem; border-bottom: 1px solid #333; }
        td:first-child { color: #888; width: 220px; }
        td:last-child { color: #4ade80; font-weight: 600; }
        .actions { margin-top: 1.5rem; display: flex; gap: 0.5rem; flex-wrap: wrap; }
        button {
            padding: 0.7rem 1.3rem;
            font-size: 0.95rem;
            cursor: pointer;
            background: #1a1a1a;
            color: #fff;
            border: 1px solid #444;
            border-radius: 6px;
        }
        button.danger { background: #dc2626; border-color: #dc2626; }
        .logs {
            margin-top: 1rem;
            max-height: 300px;
            overflow: auto;
            background: #000;
            padding: 0.75rem;
            font-size: 11px;
            border-radius: 6px;
        }
        .log-error { color: #f87171; }
        .log-warn { color: #fbbf24; }
        .log-info { color: #94a3b8; }
        .log-debug { color: #64748b; }
    `;

    render() {
        return html`
            <h2>Tanı Ekranı (Diagnostic)</h2>
            <table>
                <tr><td>device_id</td><td>${this.deviceId}</td></tr>
                <tr><td>gateway_url</td><td>${this.gatewayUrl}</td></tr>
                <tr><td>client_version</td><td>${this.version}</td></tr>
                <tr><td>bağlantı</td><td>${this.connection}</td></tr>
                <tr><td>son tick yaşı</td><td>${this.lastTickAge < 0 ? '—' : this.lastTickAge + ' sn'}</td></tr>
                <tr><td>bellek</td><td>${this.memMb} MB</td></tr>
                <tr><td>ağ</td><td>${this.network}</td></tr>
                <tr><td>sunucu offset</td><td>${this.serverOffset} ms</td></tr>
            </table>

            <div class="actions">
                <button @click=${() => { this.showLogs = !this.showLogs; }}>
                    ${this.showLogs ? 'Logları Gizle' : 'Logları Göster'}
                </button>
                <button @click=${() => {
                    const n = sendSnapshot();
                    alert(n + ' log kaydı sunucuya gönderim kuyruğuna alındı.');
                }}>
                    Snapshot Gönder
                </button>
                ${this.restricted ? '' : html`
                    <button class="danger" @click=${() => this.guard('cache', () => void clearCacheAndReload())}>
                        ${this.confirming === 'cache' ? 'Onaylamak için tekrar dokunun' : 'Cache Temizle + Yeniden Başlat'}
                    </button>
                    <button class="danger" @click=${() => this.guard('reset', () => void resetPairing())}>
                        ${this.confirming === 'reset' ? 'Eminseniz tekrar dokunun (bağlantı kesilir)' : 'Eşleştirmeyi Sıfırla'}
                    </button>
                `}
                <button @click=${() => this.remove()}>Kapat</button>
            </div>

            ${this.showLogs ? html`
                <div class="logs">
                    ${this.logs.slice().reverse().map((l) => html`
                        <div class="log-${l.level}">
                            [${new Date(l.ts).toLocaleTimeString('tr-TR')}]
                            ${l.level.toUpperCase()}: ${l.message}
                        </div>
                    `)}
                </div>
            ` : ''}
        `;
    }
}

/** JWT payload'undan device_id çıkar (imza doğrulamadan — sadece görüntüleme). */
function decodeDeviceId(): string {
    const token = localStorage.getItem('fp.device.token');
    if (!token) return '—';
    try {
        const payload = token.split('.')[1];
        if (!payload) return '—';
        const json = JSON.parse(atob(payload.replace(/-/g, '+').replace(/_/g, '/')));
        return (json.device_id as string) ?? (json.sub as string) ?? '—';
    } catch {
        return '—';
    }
}

export function openDiagnostic(restricted = false): void {
    if (document.querySelector('fp-diagnostic-overlay')) return;
    const el = document.createElement('fp-diagnostic-overlay') as FpDiagnosticOverlay;
    el.restricted = restricted;
    document.body.appendChild(el);
}
